import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Risk, RiskProbability, RiskImpact, RiskStatus, RiskRAG, Project } from '../../../types';
import {
  getProjectRisks, createRiskWithFields, updateRisk, deleteRisk, calcRiskScore,
} from '../../../services/riskService';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import { graphGet } from '../../../api/graphClient';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const PI_LABEL: Record<number, string> = {
  100000003: 'Very Low',
  100000000: 'Low',
  100000001: 'Medium',
  100000002: 'High',
  100000004: 'Very High',
};
const PI_BADGE: Record<number, { bg: string; fg: string }> = {
  100000003: { bg: '#F9FAFB', fg: '#6B7280' },
  100000000: { bg: '#F3F4F6', fg: '#5A6272' },
  100000001: { bg: '#FEF3C7', fg: '#92400E' },
  100000002: { bg: '#FEE2E2', fg: '#991B1B' },
  100000004: { bg: '#DC2626', fg: '#FFFFFF' },
};
// Display order — lowest to highest — used by all dropdowns and the
// heat map axes.
const PI_ORDER = [100000003, 100000000, 100000001, 100000002, 100000004];
const STAT_LABEL: Record<number, string> = { 100000000: 'Identified', 100000001: 'Monitoring', 100000002: 'Mitigating', 100000003: 'Closed', 100000004: 'Accepted' };
const STAT_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#D4EEF5', fg: '#0A5568' },
  100000001: { bg: '#FEF3C7', fg: '#92400E' },
  100000002: { bg: '#FEE2E2', fg: '#991B1B' },
  100000003: { bg: '#F3F4F6', fg: '#5A6272' },
  100000004: { bg: '#D1FAE5', fg: '#065F46' },
};

function scoreColor(score: number): string {
  if (score <= 2) return '#059669';
  if (score <= 4) return '#D97706';
  return '#DC2626';
}

type StatusFilter = 'all' | 100000000 | 100000001 | 100000002 | 100000003 | 100000004;
const FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all',     label: 'All' },
  { value: 100000000, label: 'Identified' },
  { value: 100000001, label: 'Monitoring' },
  { value: 100000002, label: 'Mitigating' },
  { value: 100000003, label: 'Closed' },
  { value: 100000004, label: 'Accepted' },
];

export default function RisksTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_risks">
      <RisksTabBody {...props} />
    </ModuleGuard>
  );
}

function RisksTabBody({ pca, projectId }: Props) {
  const [items, setItems]       = useState<Risk[]>([]);
  const [filter, setFilter]     = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Risk | null>(null);
  const [showNew, setShowNew]   = useState(false);
  const [view, setView]         = useState<'table' | 'heatmap'>('table');
  // Cell filter: when user clicks a heat-map cell, show only risks in that
  // probability/impact bucket. Keyed by `${prob}-${impact}` picklist values.
  const [cellFilter, setCellFilter] = useState<string | null>(null);

  const reload = () => getProjectRisks(pca, projectId).then(setItems);
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  // Backfill: for any risk with an ownerid but no ownername, resolve the
  // display name via Graph and PATCH dagrun_pm_ownername. Fire-and-forget
  // after the table has already rendered, so loading isn't blocked.
  useEffect(() => {
    const missing = items.filter(r => r.dagrun_pm_ownerid && !r.dagrun_pm_ownername);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const r of missing) {
        if (cancelled) return;
        try {
          const user = await graphGet<{ displayName?: string }>(pca, `/users/${r.dagrun_pm_ownerid}?$select=displayName`);
          const name = user.displayName;
          if (!name) continue;
          await updateRisk(pca, r.dagrun_pm_riskid, { dagrun_pm_ownername: name });
          if (cancelled) return;
          setItems(prev => prev.map(x => x.dagrun_pm_riskid === r.dagrun_pm_riskid ? { ...x, dagrun_pm_ownername: name } : x));
        } catch { /* non-fatal — skip this risk */ }
      }
    })();
    return () => { cancelled = true; };
  }, [items, pca]);

  const filtered = useMemo(() => {
    let list = filter === 'all' ? items : items.filter(r => r.dagrun_pm_status === filter);
    if (cellFilter) {
      const [p, i] = cellFilter.split('-').map(n => Number(n));
      list = list.filter(r => r.dagrun_pm_probability === p && r.dagrun_pm_impact === i);
    }
    return list;
  }, [items, filter, cellFilter]);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Risks</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{
            display: 'inline-flex', gap: 2, padding: 2,
            background: '#F5F6F8', borderRadius: 5,
          }}>
            <ViewToggleButton active={view === 'table'}   label="Table"    onClick={() => { setView('table'); setCellFilter(null); }} />
            <ViewToggleButton active={view === 'heatmap'} label="Heat Map" onClick={() => setView('heatmap')} />
          </div>
          <button type="button" onClick={() => setShowNew(true)}
            style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
            onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
          >+ Create Risk</button>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <FilterPills value={filter} options={FILTER_OPTIONS} onChange={setFilter} />
      </div>

      {view === 'heatmap' && (
        <RiskHeatMap items={items} cellFilter={cellFilter} onCellClick={k => setCellFilter(cellFilter === k ? null : k)} />
      )}

      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>No risks match this filter.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Title</Th><Th>Probability</Th><Th>Impact</Th><Th>Score</Th><Th>RAG</Th><Th>Status</Th><Th>Owner</Th>
          </tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.dagrun_pm_riskid}
                onClick={() => setSelected(r)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{r.dagrun_pm_name}</span></Td>
                <Td><Badge {...PI_BADGE[r.dagrun_pm_probability]}>{PI_LABEL[r.dagrun_pm_probability]}</Badge></Td>
                <Td><Badge {...PI_BADGE[r.dagrun_pm_impact]}>{PI_LABEL[r.dagrun_pm_impact]}</Badge></Td>
                <Td>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#5A6272' }}>
                    {r.dagrun_pm_riskscore != null
                      ? (Number(r.dagrun_pm_riskscore) * 100).toFixed(0) + '%'
                      : r.dagrun_pm_priorityscore}
                  </span>
                </Td>
                <Td>
                  {r.dagrun_pm_rag != null
                    ? <RagDot rag={r.dagrun_pm_rag} />
                    : <Em>—</Em>}
                </Td>
                <Td><Badge {...STAT_BADGE[r.dagrun_pm_status]}>{STAT_LABEL[r.dagrun_pm_status]}</Badge></Td>
                <Td>{
                  r.dagrun_pm_ownername
                    ? <span style={{ fontSize: 13, color: '#5A6272' }}>{r.dagrun_pm_ownername}</span>
                    : r.dagrun_pm_ownerid
                      ? <span style={{ fontSize: 13, color: '#9198A8', fontStyle: 'italic' }}>Unknown user</span>
                      : <Em>—</Em>
                }</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <CreateRiskModal pca={pca} projectId={projectId} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }} />
      )}
      {selected && (
        <RiskDrawer pca={pca} risk={selected}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
const Em = ({ children }: { children: React.ReactNode }) => (
  <span style={{ color: '#B8BEC9', fontSize: 13 }}>{children}</span>
);

function CreateRiskModal({ pca, projectId, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [probability, setProbability] = useState<number>(RiskProbability.Medium);
  const [impact, setImpact] = useState<number>(RiskImpact.Medium);
  const [owner, setOwner] = useState<Person | null>(null);
  const [mitigation, setMitigation] = useState('');
  const [contingency, setContingency] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createRiskWithFields(pca, projectId, {
        name: name.trim(),
        description: description.trim() || null,
        probability, impact,
        ownerid: owner?.aadObjectId ?? null,
        ownername: owner?.displayName ?? null,
        mitigationplan: mitigation.trim() || null,
        contingencyplan: contingency.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal
      open
      title="Create Risk"
      onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Creating…' : 'Create Risk'}</BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Title *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} placeholder="Short, specific risk title" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} style={BM_TEXTAREA} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Probability</label>
          <select value={probability} onChange={e => setProbability(Number(e.target.value))} style={BM_INPUT}>
            {PI_ORDER.map(v => (
              <option key={v} value={v}>{PI_LABEL[v]}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Impact</label>
          <select value={impact} onChange={e => setImpact(Number(e.target.value))} style={BM_INPUT}>
            {PI_ORDER.map(v => (
              <option key={v} value={v}>{PI_LABEL[v]}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Owner</label>
        <PeoplePicker pca={pca} value={owner} onChange={setOwner} placeholder="Assign owner…" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Mitigation Plan</label>
        <textarea value={mitigation} onChange={e => setMitigation(e.target.value)} style={BM_TEXTAREA} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Contingency Plan</label>
        <textarea value={contingency} onChange={e => setContingency(e.target.value)} style={BM_TEXTAREA} />
      </div>
      <div style={{ fontSize: 12, color: '#9198A8' }}>
        Score: <strong style={{ color: scoreColor(calcRiskScore(probability, impact)) }}>{calcRiskScore(probability, impact)}</strong>
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function RiskDrawer({ pca, risk, onClose, onDeleted }: { pca: IPublicClientApplication; risk: Risk; onClose: () => void; onDeleted: () => void }) {
  const [draft, setDraft] = useState<Risk>(risk);
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [openSection, setOpenSection] = useState({ details: true, mitigation: true, contingency: true });
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(risk); setOwnerName(risk.dagrun_pm_ownername ?? null); }, [risk]);

  // If we have an ownerid but no resolved name, resolve via Graph so the
  // PeoplePicker doesn't render a raw GUID as the displayName.
  useEffect(() => {
    if (!draft.dagrun_pm_ownerid || ownerName) return;
    let cancelled = false;
    graphGet<{ displayName?: string }>(pca, `/users/${draft.dagrun_pm_ownerid}?$select=displayName`)
      .then(u => {
        if (cancelled || !u.displayName) return;
        setOwnerName(u.displayName);
        setDraft(d => ({ ...d, dagrun_pm_ownername: u.displayName ?? null }));
        updateRisk(pca, draft.dagrun_pm_riskid, { dagrun_pm_ownername: u.displayName }).catch(() => { /* non-fatal */ });
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [draft.dagrun_pm_ownerid, draft.dagrun_pm_riskid, ownerName, pca]);

  const patch = async (fields: Partial<Risk>) => {
    setSaving(true);
    try {
      await updateRisk(pca, draft.dagrun_pm_riskid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };

  // Probability/Impact change → recompute score and PATCH both.
  const setProbImpact = (k: 'dagrun_pm_probability' | 'dagrun_pm_impact', v: number) => {
    const next = { ...draft, [k]: v };
    next.dagrun_pm_priorityscore = calcRiskScore(next.dagrun_pm_probability, next.dagrun_pm_impact);
    setDraft(next);
    patch({ [k]: v, dagrun_pm_priorityscore: next.dagrun_pm_priorityscore } as Partial<Risk>);
  };

  const setAndPatch = <K extends keyof Risk>(k: K, v: Risk[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    patch({ [k]: v } as Partial<Risk>);
  };

  const setTextDebounced = <K extends keyof Risk>(k: K, v: Risk[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<Risk>), 600);
  };

  const flushText = <K extends keyof Risk>(k: K, v: Risk[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<Risk>);
  };

  const ownerPerson: Person | null = draft.dagrun_pm_ownerid
    ? { aadObjectId: draft.dagrun_pm_ownerid, displayName: ownerName ?? 'Resolving…' }
    : null;

  const doDelete = async () => {
    setSaving(true);
    try { await deleteRisk(pca, draft.dagrun_pm_riskid); onDeleted(); } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>{savedFlash ? 'Saved ✓' : ''}</span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <input
          value={draft.dagrun_pm_name}
          onChange={e => setTextDebounced('dagrun_pm_name', e.target.value)}
          onBlur={e => flushText('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 8, width: '100%', background: 'transparent' }}
        />

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={draft.dagrun_pm_probability}
            onChange={e => setProbImpact('dagrun_pm_probability', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {PI_ORDER.map(v => (
              <option key={v} value={v}>P: {PI_LABEL[v]}</option>
            ))}
          </select>
          <select value={draft.dagrun_pm_impact}
            onChange={e => setProbImpact('dagrun_pm_impact', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {PI_ORDER.map(v => (
              <option key={v} value={v}>I: {PI_LABEL[v]}</option>
            ))}
          </select>
          <select value={draft.dagrun_pm_status}
            onChange={e => setAndPatch('dagrun_pm_status', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[RiskStatus.Identified, RiskStatus.Monitoring, RiskStatus.Mitigating, RiskStatus.Closed, RiskStatus.Accepted].map(v => (
              <option key={v} value={v}>{STAT_LABEL[v]}</option>
            ))}
          </select>
          <span style={{ fontSize: 12, color: '#9198A8', marginLeft: 4 }}>
            Score: <strong style={{ color: scoreColor(draft.dagrun_pm_priorityscore), fontFamily: 'var(--font-mono)' }}>{draft.dagrun_pm_priorityscore}</strong>
          </span>
        </div>

        <Section title="Details" open={openSection.details} onToggle={() => setOpenSection(s => ({ ...s, details: !s.details }))}>
          <div style={{ marginBottom: 12 }}>
            <label style={BM_LABEL}>Owner</label>
            <PeoplePicker pca={pca} value={ownerPerson}
              onChange={p => {
                setDraft(d => ({ ...d, dagrun_pm_ownerid: p?.aadObjectId ?? null, dagrun_pm_ownername: p?.displayName ?? null }));
                setOwnerName(p?.displayName ?? null);
                patch({ dagrun_pm_ownerid: p?.aadObjectId ?? null, dagrun_pm_ownername: p?.displayName ?? null });
              }}
              placeholder="Select owner…"
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={BM_LABEL}>Description</label>
            <textarea value={draft.dagrun_pm_description ?? ''}
              onChange={e => setTextDebounced('dagrun_pm_description', e.target.value)}
              onBlur={e => flushText('dagrun_pm_description', e.target.value)}
              style={BM_TEXTAREA}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!draft.dagrun_pm_clientvisible}
              onChange={e => setAndPatch('dagrun_pm_clientvisible', e.target.checked)} />
            Visible to clients
          </label>
        </Section>

        <Section title="Mitigation Plan" open={openSection.mitigation} onToggle={() => setOpenSection(s => ({ ...s, mitigation: !s.mitigation }))}>
          <textarea value={draft.dagrun_pm_mitigationplan ?? ''}
            onChange={e => setTextDebounced('dagrun_pm_mitigationplan', e.target.value)}
            onBlur={e => flushText('dagrun_pm_mitigationplan', e.target.value)}
            style={BM_TEXTAREA}
          />
        </Section>

        <Section title="Contingency Plan" open={openSection.contingency} onToggle={() => setOpenSection(s => ({ ...s, contingency: !s.contingency }))}>
          <textarea value={draft.dagrun_pm_contingencyplan ?? ''}
            onChange={e => setTextDebounced('dagrun_pm_contingencyplan', e.target.value)}
            onBlur={e => flushText('dagrun_pm_contingencyplan', e.target.value)}
            style={BM_TEXTAREA}
          />
        </Section>

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete Risk</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal
          open
          title="Delete Risk"
          onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}
        >
          Delete <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_name}</strong>? This cannot be undone.
        </BoardModal>
      )}
    </>
  );
}

// ── Heat Map view ──────────────────────────────────────────────────
// 5×5 grid of probability × impact buckets with a count of risks in each
// cell. Cells use the standard 5×5 risk-matrix color zones:
//   bottom-left triangle (low prob AND low impact) = green
//   top-right triangle   (high prob AND high impact) = red
//   diagonal band        (anywhere else)             = amber
// Both axes use the 5-level probability/impact picklist (same option set).
function RiskHeatMap({ items, cellFilter, onCellClick }: {
  items: Risk[];
  cellFilter: string | null;
  onCellClick: (key: string) => void;
}) {
  // Rows: Probability, top=VeryHigh, bottom=VeryLow.
  // Cols: Impact,     left=VeryLow,  right=VeryHigh.
  const probs = [RiskProbability.VeryHigh, RiskProbability.High, RiskProbability.Medium, RiskProbability.Low, RiskProbability.VeryLow];
  const imps  = [RiskImpact.VeryLow, RiskImpact.Low, RiskImpact.Medium, RiskImpact.High, RiskImpact.VeryHigh];

  // Standard 5×5 risk-matrix colour zones. rowIdx 0 is top (VeryHigh prob),
  // rowIdx 4 is bottom (VeryLow prob). colIdx 0 is left (VeryLow impact),
  // colIdx 4 is right (VeryHigh impact).
  const zoneFor = (rowIdx: number, colIdx: number): 'green' | 'amber' | 'red' => {
    const probRank   = 5 - rowIdx;   // VeryHigh=5 at top, VeryLow=1 at bottom
    const impactRank = colIdx + 1;   // VeryLow=1 at left, VeryHigh=5 at right
    const product    = probRank * impactRank;  // 1..25
    if (product <= 4)   return 'green';
    if (product <= 12)  return 'amber';
    return 'red';
  };
  const zoneColor: Record<'green' | 'amber' | 'red', string> = {
    green: 'rgba(22,163,74,0.2)',
    amber: 'rgba(217,119,6,0.2)',
    red:   'rgba(220,38,38,0.2)',
  };

  // Bucket counts
  const counts = new Map<string, number>();
  items.forEach(r => {
    if (r.dagrun_pm_probability == null || r.dagrun_pm_impact == null) return;
    const k = `${r.dagrun_pm_probability}-${r.dagrun_pm_impact}`;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  });
  const unscored = items.filter(r => r.dagrun_pm_probability == null || r.dagrun_pm_impact == null).length;

  return (
    <div style={{
      background: '#fff', border: '1px solid #EAECF0', borderRadius: 8,
      padding: 20, marginBottom: 20,
    }}>
      <div style={{ fontSize: 12, color: '#5A6272', marginBottom: 12 }}>
        Probability × Impact matrix. Click a cell to filter the table below.
        {cellFilter && (
          <button type="button" onClick={() => onCellClick(cellFilter)}
            style={{ marginLeft: 10, background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
          >Clear filter</button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(5, 1fr)', gap: 4 }}>
        {/* Header row */}
        <div />
        {imps.map(i => (
          <div key={`h-${i}`} style={{
            textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#5A6272',
            textTransform: 'uppercase', letterSpacing: '0.06em', paddingBottom: 6,
          }}>{PI_LABEL[i]}</div>
        ))}
        {probs.map((p, rowIdx) => (
          <React.Fragment key={`r-${p}`}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: '#5A6272',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              paddingRight: 10,
            }}>{PI_LABEL[p]}</div>
            {imps.map((i, colIdx) => {
              const key = `${p}-${i}`;
              const count = counts.get(key) ?? 0;
              const zone = zoneFor(rowIdx, colIdx);
              const active = cellFilter === key;
              return (
                <button key={key} type="button" onClick={() => onCellClick(key)}
                  style={{
                    background: zoneColor[zone],
                    border: active ? '2px solid #0E7490' : '1px solid rgba(0,0,0,0.05)',
                    borderRadius: 6,
                    padding: '18px 8px',
                    minHeight: 64,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 20, fontWeight: 700,
                    color: count > 0 ? '#0F1117' : '#C4CAD4',
                    cursor: 'pointer',
                    transition: 'transform 0.08s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                >{count > 0 ? count : '·'}</button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#9198A8', marginTop: 10, fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
        ← Impact →
      </div>
      {unscored > 0 && (
        <div style={{ fontSize: 12, color: '#9198A8', marginTop: 14, fontStyle: 'italic' }}>
          {unscored} risk{unscored === 1 ? '' : 's'} unscored — add probability and impact to include them.
        </div>
      )}
    </div>
  );
}

function ViewToggleButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        border: 'none',
        background: active ? '#D4EEF5' : 'transparent',
        color: active ? '#0E7490' : '#5A6272',
        fontWeight: active ? 500 : 400,
        fontFamily: 'var(--font-ui)',
        fontSize: 12, padding: '5px 12px',
        borderRadius: 4, cursor: 'pointer',
      }}
    >{label}</button>
  );
}

function RagDot({ rag }: { rag: number }) {
  const color = rag === RiskRAG.Green ? '#16A34A'
    : rag === RiskRAG.Amber ? '#D97706'
    : rag === RiskRAG.Red   ? '#DC2626'
    : '#B8BEC9';
  const label = rag === RiskRAG.Green ? 'Green'
    : rag === RiskRAG.Amber ? 'Amber'
    : rag === RiskRAG.Red   ? 'Red'
    : '—';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 5, background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: '#5A6272' }}>{label}</span>
    </span>
  );
}

function Section({ title, open, onToggle, children }: { title: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div>
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11, fontWeight: 600, color: '#5A6272',
        textTransform: 'uppercase', letterSpacing: '0.12em',
        cursor: 'pointer', userSelect: 'none',
        padding: '12px 0 0', borderTop: '0.5px solid #EAECF0', marginTop: 16,
      }}>
        <span>{title}</span>
        <span style={{ fontSize: 10, color: '#9198A8' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={{ paddingTop: 12 }}>{children}</div>}
    </div>
  );
}
